import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

import { supabase } from '@/lib/customSupabaseClient';
import { useToast } from '@/components/ui/use-toast';

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
  const { toast } = useToast();

  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [activeAccountId, setActiveAccountId] = useState(null);
  const [subaccounts, setSubaccounts] = useState([]);

  const handleSession = useCallback(async (session) => {
    console.log('AuthContext: Atualizando sessão:', session ? 'Ativa' : 'Inativa');

    if (session?.user) {
      console.log('AuthContext: Usuário autenticado:', session.user.id);
      
      // Fetch subaccounts
      const { data: subs, error } = await supabase
        .from('subaccounts')
        .select('*')
        .eq('parent_user_id', session.user.id);
        
      if (!error && subs) {
        setSubaccounts(subs);
      }
      
      // Initialize active account, respecting localStorage if valid
      const savedAccountId = localStorage.getItem('conciliador_active_account');
      const isValidSaved = savedAccountId && (savedAccountId === session.user.id || subs?.find(s => s.id === savedAccountId));
      
      if (isValidSaved) {
        setActiveAccountId(savedAccountId);
      } else {
        setActiveAccountId(session.user.id);
        localStorage.setItem('conciliador_active_account', session.user.id);
      }
    } else {
      console.log('AuthContext: Nenhum usuário autenticado');
      setActiveAccountId(null);
      setSubaccounts([]);
      localStorage.removeItem('conciliador_active_account');
    }
    
    // Apply session and user states AFTER all context is ready to prevent premature Dashboard render
    setSession(session);
    setUser(session?.user ?? null);
    setLoading(false);
  }, []);

  // Função auxiliar para limpar tokens
  const clearInvalidTokens = useCallback(async () => {
    console.log('Limpando tokens inválidos...');
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.includes('supabase') || key.includes('auth')) {
          localStorage.removeItem(key);
        }
      });
      localStorage.removeItem('conciliador_active_account');

      await supabase.auth.signOut();
      console.log('Tokens limpos com sucesso');
    } catch (error) {
      console.log('Erro ao limpar tokens:', error);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    let retryCount = 0;
    const maxRetries = 3;

    const getSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.log('Auth session error:', error.message);

          // Se é erro de refresh token, tentar limpar e forçar logout limpo
          if (error.message && (
            error.message.includes('refresh_token_not_found') ||
            error.message.includes('Invalid Refresh Token')
          )) {
            console.log('Token inválido, executando limpeza...');
            await clearInvalidTokens();
            if (mounted) handleSession(null);
            return;
          }

          // Retry logic
          if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(() => {
              if (mounted) getSession();
            }, 2000);
            return;
          }
        }

        if (mounted) {
          handleSession(session);
        }
      } catch (error) {
        console.log('Auth exception:', error);
        if (mounted) handleSession(null);
      }
    };

    getSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state change:', event);

        if (event === 'SIGNED_OUT') {
          if (mounted) {
            handleSession(null);
            setRecoveryMode(false);
          }
        } else if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
          if (mounted) handleSession(session);
        } else if (event === 'USER_UPDATED') {
          if (mounted) handleSession(session);
        } else if (event === 'PASSWORD_RECOVERY') {
          console.log('AuthContext: Entrando em modo de recuperação de senha');
          if (mounted) setRecoveryMode(true);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [handleSession, clearInvalidTokens]);

  const signUp = useCallback(async (email, password, options) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options,
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Erro ao criar conta",
        description: error.message || "Algo deu errado ao tentar criar sua conta.",
      });
    }

    return { error };
  }, [toast]);

  const signIn = useCallback(async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      let errorMessage = error.message || "Algo deu errado ao tentar entrar.";
      let errorTitle = "Erro ao entrar";

      if (errorMessage.includes("Email not confirmed")) {
        errorTitle = "E-mail não confirmado";
        errorMessage = "Você precisa confirmar sua inscrição. Verifique seu e-mail e a caixa de Spam.";
      } else if (errorMessage.includes("Invalid login credentials")) {
        errorTitle = "E-mail ou senha inválidos";
        errorMessage = "Tente novamente";
      }

      toast({
        variant: "destructive",
        title: errorTitle,
        description: errorMessage,
      });
    }

    return { error };
  }, [toast]);

  const resetPassword = useCallback(async (email) => {
    // Detectar ambiente para o redirecionamento correto
    const isDev = window.location.hostname === 'localhost';
    const redirectTo = isDev
      ? 'http://localhost:5173/update-password'
      : 'https://app.conferecartao.com.br/update-password';

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Erro ao solicitar recuperação",
        description: error.message,
      });
    } else {
      toast({
        title: "E-mail enviado!",
        description: "Verifique seu e-mail para redefinir sua senha.",
      });
    }

    return { error };
  }, [toast]);

  const signOut = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut();

      // Sempre limpar local storage para garantir
      await clearInvalidTokens();

      if (error) {
        // Ignorar erro específico de sessão inexistente
        if (!error.message?.includes('session_id claim')) {
          toast({
            variant: "destructive",
            title: "Erro ao sair",
            description: error.message,
          });
        }
      }

      setSession(null);
      setUser(null);

      return { error: null };
    } catch (err) {
      console.log('Erro no signOut:', err);
      return { error: err };
    }
  }, [toast, clearInvalidTokens]);

  const updateUserMetadata = useCallback(async (metadata) => {
    const { data, error } = await supabase.auth.updateUser({
      data: metadata
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Erro ao atualizar informações",
        description: error.message,
      });
      throw error;
    }

    if (data?.user) {
      setUser(data.user);
      setSession((prev) => prev ? { ...prev, user: data.user } : prev);
    }

    return { data, error };
  }, [toast]);

  const updateUserEmail = useCallback(async (email) => {
    const { error } = await supabase.auth.updateUser({ email });
    if (error) {
      toast({ variant: "destructive", title: "Erro ao atualizar e-mail", description: error.message });
      throw error;
    }
    return { error };
  }, [toast]);

  const updateUserPassword = useCallback(async (password) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      toast({ variant: "destructive", title: "Erro ao atualizar senha", description: error.message });
      throw error;
    }
    return { error };
  }, [toast]);

  const value = useMemo(() => ({
    user,
    session,
    loading,
    recoveryMode,
    setRecoveryMode,
    activeAccountId,
    setActiveAccountId,
    subaccounts,
    refreshSubaccounts: async () => {
      if (user) {
        const { data } = await supabase.from('subaccounts').select('*').eq('parent_user_id', user.id);
        if (data) setSubaccounts(data);
      }
    },
    signUp,
    signIn,
    resetPassword,
    signOut,
    updateUserMetadata,
    updateUserEmail,
    updateUserPassword,
  }), [user, session, loading, recoveryMode, setRecoveryMode, activeAccountId, subaccounts, signUp, signIn, resetPassword, signOut, updateUserMetadata, updateUserEmail, updateUserPassword]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { supabase };
